"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";

interface Props {
  onClose: () => void;
}

const statusOptions = [
  { value: "backlog",     label: "Backlog" },
  { value: "planning",   label: "Planning" },
  { value: "brainstorm", label: "Brainstorm" },
  { value: "active",     label: "In Progress" },
  { value: "waiting",    label: "Waiting" },
  { value: "blocked",    label: "Blocked" },
  { value: "wont_do",    label: "Won't Do" },
];

const assigneeOptions = [
  { value: null, label: "Unassigned" },
  { value: "robby", label: "Robby" },
  { value: "weston", label: "Weston" },
];

export default function AddProjectModal({ onClose }: Props) {
  const router = useRouter();
  const [type, setType] = useState<"project" | "task">("task");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("backlog");
  const [notes, setNotes] = useState("");
  const [assignedTo, setAssignedTo] = useState<"robby" | "weston" | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const res = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, name, description, status, notes, assignedTo }),
      });
      if (!res.ok) throw new Error("Save failed");
      router.refresh();
      onClose();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-[#2c2416]/30 backdrop-blur-[2px] z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
        <div className="bg-[#faf8f4] border border-[#e4ddd0] rounded-xl shadow-xl w-full max-w-lg">

          {/* Header */}
          <div className="flex items-center justify-between px-7 py-5 border-b border-[#e8e2d8]">
            <h2 className="font-serif text-[20px] text-[#2c2416]">New Card</h2>
            <button onClick={onClose} className="text-[#b0a090] hover:text-[#6b5c48] text-xl leading-none transition-colors">×</button>
          </div>

          <form onSubmit={handleSubmit} className="px-7 py-6 flex flex-col gap-5">

            {/* Type toggle */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">
                Type
              </label>
              <div className="flex gap-2">
                {(["task", "project"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex-1 py-2 rounded-lg text-[13px] font-medium border transition-all ${
                      type === t
                        ? "bg-[#b5622a] text-white border-[#b5622a]"
                        : "bg-white text-[#6b5c48] border-[#e4ddd0] hover:border-[#b5622a]"
                    }`}
                  >
                    {t === "task" ? "✅ Task" : "📁 Project"}
                  </button>
                ))}
              </div>
              {type === "project" && (
                <p className="text-[11px] text-[#a0906e] mt-2 italic">
                  Projects support subtasks. You can add them after creating the project.
                </p>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">
                Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={type === "project" ? "What are you building?" : "What needs doing?"}
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] placeholder-[#c0b8b0] focus:outline-none focus:border-[#b5622a] transition-colors"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's the goal?"
                rows={3}
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] placeholder-[#c0b8b0] focus:outline-none focus:border-[#b5622a] transition-colors resize-none"
              />
            </div>

            {/* Column */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">
                Column
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] focus:outline-none focus:border-[#b5622a] transition-colors"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">
                Notes <span className="font-normal normal-case tracking-normal text-[#b0a090]">(optional)</span>
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any extra context…"
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] placeholder-[#c0b8b0] focus:outline-none focus:border-[#b5622a] transition-colors"
              />
            </div>

            {/* Assigned To */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">Assigned To</label>
              <select
                value={assignedTo ?? ""}
                onChange={(e) => setAssignedTo(e.target.value === "" ? null : (e.target.value as "robby" | "weston"))}
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] focus:outline-none focus:border-[#b5622a] transition-colors"
              >
                {assigneeOptions.map((opt) => (
                  <option key={opt.value ?? "null"} value={opt.value ?? ""}>{opt.label}</option>
                ))}
              </select>
            </div>

            {error && <p className="text-[12px] text-red-500">{error}</p>}

            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={onClose} className="px-5 py-2 text-[13px] text-[#6b5c48] hover:text-[#2c2416] transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-[#b5622a] text-white text-[13px] font-medium rounded-lg hover:bg-[#9e5424] transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : `Add ${type === "project" ? "Project" : "Task"}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
